import React from "react";
import { Controller, Control, FieldValues, Path } from "react-hook-form";
import MaterialSelect, { SelectChangeEvent } from "@mui/material/Select";
import ClearIcon from "@mui/icons-material/Clear";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";

// Component
import FieldErrorMessage from "../../UI/FieldErrorMessage";
import { IconButton } from "@mui/material";

export interface SelectFormGroupProps<TFieldValues extends FieldValues = FieldValues> {
  name: Path<TFieldValues>;
  value?: any;
  label: string;
  control: Control<TFieldValues, any>;
  rules: any;
  errors: any;
  onChange: (event: SelectChangeEvent<any>) => void;
  onClear?: () => void;
  options: { label: string; value: any; icon?: any; disabled?: boolean }[];
  required?: boolean;
  noOptionText?: string;
  MenuProps?: any;
  multiple?: boolean;
}

const SelectFormGroup = <TFieldValues extends FieldValues = FieldValues>({
  label,
  required,
  control,
  rules,
  errors,
  name,
  value,
  onChange,
  onClear,
  options,
  noOptionText = "No options",
  MenuProps,
  multiple = false,
}: SelectFormGroupProps<TFieldValues>) => {
    return (
      <div className="form-group custom-form-group">
        <label>{`${label}${required ? " *" : ""}`}</label>
        <Controller
          control={control}
          name={name}
          rules={rules}
          render={({ field }) => (
            <MaterialSelect
              {...field}
              multiple={multiple}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300,
                    width: 250,
                  },
                  ...MenuProps?.PaperProps
                },
              }}
              style={{
                background: "#eff3f7",
                borderRadius: "6px",
                ...(multiple && { height: 'auto', minHeight: '40px' }),
              }}
              sx={multiple ? {
                '& .MuiSelect-select': {
                  whiteSpace: 'normal',
                  paddingTop: '8px',
                  paddingBottom: '8px',
                }
              } : {}}
              value={multiple ? (field.value ?? []) : (field.value ?? '')}
              renderValue={multiple ? (selected: any) => {
                const selectedArr: any[] = Array.isArray(selected) ? selected : [];
                return selectedArr
                  .map(val => options.find(o => o.value === val)?.label ?? val)
                  .join(', ');
              } : undefined}
              onChange={(e) => {
                field.onChange(e);
                onChange?.(e);
              }}
              endAdornment={
                (multiple ? field.value?.length > 0 : (field.value || field.value === 0)) && onClear ? (
                  <IconButton
                    onClick={onClear}
                    sx={{
                      mr: -1,
                      background: "#eff3f7",
                      ":hover": { background: "#eff3f7" },
                    }}
                  >
                    <ClearIcon />
                  </IconButton>
                ) : null
              }
            >
              {options.length <= 0 && (
                <MenuItem disabled>{noOptionText}</MenuItem>
              )}
              {options.map((item, i) => (
                <MenuItem
                  key={(item?.value || item?.label || item) + `${i}`}
                  value={item?.value === 0 ? 0 : item?.value || item?.label || item}
                  disabled={item?.disabled}
                >
                  {multiple ? (
                    <>
                      <Checkbox
                        checked={Array.isArray(field.value) && field.value.includes(item?.value === 0 ? 0 : item?.value || item?.label || item)}
                        size="small"
                        sx={{ p: 0, mr: 1 }}
                      />
                      <ListItemText primary={item?.label} />
                    </>
                  ) : (
                    <>
                      {item?.icon ? <img width="30px" src={item.icon} alt="" /> : null}
                      {item?.label}
                    </>
                  )}
                </MenuItem>
              ))}
            </MaterialSelect>
          )}
        />
        <FieldErrorMessage errors={errors} name={name} containerClass="w-100" />
      </div>
    );
};

export default SelectFormGroup;
